/* gc-track.js: GENTCREATE interaction events for GTM, GA4 and Microsoft Clarity (4 Oct 2026).

   Loaded once, deferred, from layout/theme.liquid. The script tag carries the page context:
     data-template  "product", "product.personalized-case", "product.leather-set", "cart", ...
     data-pid       the product id on product pages (the same in every language)
     data-handle    the product handle
     data-lang      the storefront language
   Docs: docs/analytics/events-2026-10-04/EVENTS.md (event list, GTM import, GA4 definitions).

   Every event is
     dataLayer.push({gc: null});                         (clears the last event's params in GTM)
     dataLayer.push({event: 'gc_<name>', gc: {...}});
   and, when Clarity is on the page, clarity('event', 'gc_<name>') so recordings and heatmaps can be
   filtered by it. One GTM Custom Event trigger (^gc_) and one GA4 tag forward them all.

   HOOKS. Nothing here depends on CSS class names:
     - data attributes the theme already prints: data-gc-set-tag (Shop the Bundle), data-gc-support and
       data-gc-support-clip (support tile and its video), data-open-personalize-drawer,
       data-gcpx-ps-spot (PDP photo initials spot), data-gc-addon-check and the add-on rows'
       data-gc-gift-addon / data-gc-priority-addon / data-gc-sp-addon / data-gc-ms-addon,
       data-gc-sp-add, data-gc-undo-go, data-section-type="product" + data-media-id (gallery slides),
       data-gcls-slide (Leather Set photos), name="checkout" (cart checkout buttons);
     - generic data-gc-track="<name>" on any element (a click pushes gc_<name>);
     - DOM events the components dispatch: personalization:added / :removed (personalize drawer) and
       gc:track, a one-line CustomEvent the components send at the right moment:
         document.dispatchEvent(new CustomEvent('gc:track', {detail: {name: 'builder_choice', ...}}))
     - Elevar's own dl_add_to_cart (read from the dataLayer) for the add-to-cart context.

   PRIVACY: the initials themselves are never sent, only their length and fee tier.
   PERFORMANCE: no dependencies, passive listeners, no scroll handlers, no layout reads; the image
   module uses IntersectionObserver entries only. Every handler is wrapped so it can never break a page. */
(function () {
  'use strict';
  var W = window, D = document;
  if (W.__gcTrack) return;

  var me = D.currentScript;
  var ds = (me && me.dataset) || {};
  var TPL = String(ds.template || '');
  var PAGE = TPL === 'product.personalized-case' ? 'case_builder'
    : TPL === 'product.leather-set' ? 'set_builder'
    : TPL.indexOf('product') === 0 ? 'pdp'
    : (TPL.split('.')[0] || 'other');
  var PID = String(ds.pid || '');
  var BASE = {
    page_type: PAGE,
    product_id: PID || undefined,
    product_handle: ds.handle || undefined,
    lang: ds.lang || undefined
  };
  var DEBUG = /[?&]gc_debug=1\b/.test(location.search);

  /* event names a component may send through gc:track (anything else is ignored) */
  var NAMES = {
    builder_choice: 1, builder_blocked: 1, builder_add: 1, order_check: 1, initials_spot_open: 1,
    initials_set: 1, initials_remove: 1, addon_toggle: 1, sticky_click: 1, cart_undo: 1,
    cart_upsell_open: 1, cart_upsell_add: 1, cart_drawer_open: 1, personalize_open: 1, support_click: 1, support_video: 1,
    bundle_link_click: 1, image_dwell: 1, add_to_cart_context: 1, checkout_context: 1
  };
  var CAP = { image_dwell: 25 }; /* per page view */
  var counts = {};
  var log = [];

  function now() { return Date.now(); }
  function screenClass() {
    var w = W.innerWidth || 0;
    return w < 768 ? 'phone' : w < 1024 ? 'tablet' : 'desktop';
  }
  /* the tier's US$ label, one per rung of the ladder (Oct 2026: eight characters, $11 a character past three;
     6 to 8 were all reported as 55) */
  function feeTier(n) { return n >= 8 ? '88' : n === 7 ? '77' : n === 6 ? '66' : n === 5 ? '55' : n === 4 ? '44' : n >= 1 ? '33' : undefined; }
  function iniLen(s) { return String(s || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 8).length; }
  function slug(s, max) {
    return String(s || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9\u0080-￿]+/g, '-').replace(/^-+|-+$/g, '').slice(0, max || 40);
  }

  function push(name, p, opt) {
    try {
      if (!NAMES[name]) return;
      var n = counts[name] = (counts[name] || 0) + 1;
      if (CAP[name] && n > CAP[name]) return;
      if ((counts._all = (counts._all || 0) + 1) > 200) return; /* runaway guard, per page view */
      var gc = { screen: screenClass() }, k, v;
      for (k in BASE) if (BASE[k] !== undefined) gc[k] = BASE[k];
      for (k in p) {
        if (!Object.prototype.hasOwnProperty.call(p, k)) continue;
        v = p[k];
        if (v === undefined || v === null || v === '' || typeof v === 'object' || typeof v === 'function') continue;
        if (typeof v === 'boolean') v = v ? 'yes' : 'no';
        gc[k] = typeof v === 'string' ? v.slice(0, 100) : v;
      }
      if (gc.initials_len && !gc.initials_fee) gc.initials_fee = feeTier(+gc.initials_len);
      var dl = W.dataLayer = W.dataLayer || [];
      dl.push({ gc: null });
      dl.push({ event: 'gc_' + name, gc: gc });
      if (!(opt && opt.quiet) && typeof W.clarity === 'function') {
        try { W.clarity('event', 'gc_' + name); } catch (e) {}
      }
      log.push([name, gc]);
      if (log.length > 60) log.shift();
      if (DEBUG && W.console) console.info('[gc-track]', 'gc_' + name, gc);
    } catch (e) { /* never let measurement break the page */ }
  }

  /* ---------------------------------------------------------------- images: screen time */
  /* An image "has focus" while the tab is visible and at least 60% of it is in view (or it fills
     60% of the viewport's height, for a picture taller than the screen). A gallery slide that is not
     the current one is off screen inside its carousel, so it never qualifies. When an image loses
     focus after 1 s or more, gc_image_dwell is sent (25 per page view at most) and the time is added
     to this tab's record (sessionStorage), which the add-to-cart and checkout contexts read. */
  var SKEY = 'gc_img_v1';
  function store() {
    try { return JSON.parse(W.sessionStorage.getItem(SKEY) || '') || { v: {} }; } catch (e) { return { v: {} }; }
  }
  function save(s) {
    try {
      var keys = Object.keys(s.v);
      if (keys.length > 40) {
        keys.sort(function (a, b) { return s.v[b].ms - s.v[a].ms; });
        keys.slice(40).forEach(function (k) { delete s.v[k]; });
      }
      W.sessionStorage.setItem(SKEY, JSON.stringify(s));
    } catch (e) {}
  }
  function record(t, ms) {
    var s = store(), key = (t.pid || '-') + ':' + t.id;
    var r = s.v[key] || (s.v[key] = { ms: 0, pid: t.pid || '', id: t.id, pos: t.pos || 0 });
    r.ms += ms;
    s.last = key;
    save(s);
  }

  var targets = [];          /* {el, id, pos, label, bundle, spot, pid, frame, ratioOk, since} */
  var io = null;
  function visibleDoc() { return D.visibilityState !== 'hidden'; }
  /* a carousel slide counts only while it is the gallery's selected slide (Flickity's own state, read
     through its API: on wide screens the next slide can peek in beside the current one) */
  function carousel(t) {
    if (!t.car || !W.Flickity || !W.Flickity.data) return null;
    var f = W.Flickity.data(t.car);
    if (f && !f.__gcOn) {
      f.__gcOn = 1;
      try { f.on('select', function () { targets.forEach(update); }); } catch (e) {}
    }
    return f || null;
  }
  var carWait = true; /* until 1.5 s after load: a slide waits for its carousel to start */
  /* Leather Set photos: a slide counts while the Photos panel is shown (its own gcls:view event) and it
     is the photo its thumbnail strip marks aria-current (the panel is laid out, hidden, behind the set) */
  var gclsShown = false;
  function gclsCurrent(t) {
    if (!gclsShown) return false;
    var th = D.querySelector('[data-gcls-thumb][aria-current="true"]');
    return !th || th.getAttribute('data-gcls-thumb') === t.gcls;
  }
  function selected(t) {
    if (t.gcls != null) return gclsCurrent(t);
    var f = carousel(t);
    if (!f) {
      if (!t.car) return true;
      if (carWait) return false;
      t.car = null; /* no carousel on this layout (a stacked gallery): every slide counts */
      return true;
    }
    var s = f.selectedElements || (f.selectedElement ? [f.selectedElement] : null);
    return !s || s.indexOf(t.el) !== -1;
  }
  function eligible(t) {
    return t.ratioOk && visibleDoc() && !!t.id && t.el.getAttribute('aria-hidden') !== 'true' && selected(t);
  }
  function stop(t, at) {
    if (!t.since) return;
    var ms = (at || now()) - t.since;
    t.since = 0;
    if (ms < 1000 || !t.id) return;
    ms = Math.min(Math.round(ms / 100) * 100, 30 * 60 * 1000);
    record(t, ms);
    push('image_dwell', {
      media_id: t.id, media_pos: t.pos, media_label: t.label, dwell_ms: ms,
      is_bundle_image: t.bundle, has_initials_spot: t.spot, surface: t.surface || PAGE
    }, { quiet: true });
  }
  function update(t) {
    var ok = eligible(t);
    if (ok && !t.since) t.since = now();
    else if (!ok && t.since) stop(t);
  }
  function onIO(entries) {
    try {
      var vh = W.innerHeight || 0;
      entries.forEach(function (en) {
        var t = en.target.__gcT;
        if (!t) return;
        var ih = en.intersectionRect ? en.intersectionRect.height : 0;
        var bh = en.boundingClientRect ? en.boundingClientRect.height : 0;
        t.ratioOk = en.isIntersecting && (en.intersectionRatio >= 0.6 || (vh && ih >= 0.6 * Math.min(bh, vh)));
        update(t);
      });
    } catch (e) {}
  }
  function observe(el, info) {
    if (!el || el.__gcT) return el && el.__gcT;
    var t = { el: el, ratioOk: false, since: 0 };
    for (var k in info) t[k] = info[k];
    el.__gcT = t;
    targets.push(t);
    if (!io && 'IntersectionObserver' in W) io = new IntersectionObserver(onIO, { threshold: [0, 0.3, 0.5, 0.6, 0.75, 1] });
    if (io) io.observe(el);
    return t;
  }
  function altLabel(el) {
    var im = el.querySelector && el.querySelector('img');
    return slug((im && im.getAttribute('alt')) || '', 40);
  }
  function fileId(u) {
    var f = String(u || '').split('?')[0].split('/').pop() || '';
    return f.replace(/\.[a-z0-9]+$/i, '').slice(0, 60);
  }
  function scanImages() {
    /* standard product pages: the main product section's image slides */
    var sec = PID ? D.querySelector('[data-section-type="product"][data-product-id="' + PID + '"]') : null;
    if (sec) {
      [].forEach.call(sec.querySelectorAll('[data-media-type="image"][data-media-id]'), function (el) {
        observe(el, {
          car: el.closest('[data-flickity-config]'),
          id: String(el.getAttribute('data-media-id')), pos: +el.getAttribute('data-media-position') || 0,
          label: altLabel(el), pid: PID,
          bundle: !!el.querySelector('[data-gc-set-tag]'), spot: !!el.querySelector('[data-gcpx-ps-spot]')
        });
      });
    }
    /* Leather Set page: the Photos panel */
    var ph = D.querySelector('[data-gcls-photos]');
    if (ph && !ph.__gcV) {
      ph.__gcV = 1;
      ph.addEventListener('gcls:view', function (e) { gclsShown = !!(e && e.detail && e.detail.shown); targets.forEach(update); });
      var th = D.querySelector('[data-gcls-thumb]');
      if (th && th.parentNode && W.MutationObserver) {
        new MutationObserver(function () { targets.forEach(update); })
          .observe(th.parentNode, { attributes: true, subtree: true, attributeFilter: ['aria-current'] });
      }
    }
    [].forEach.call(D.querySelectorAll('[data-gcls-slide]'), function (el) {
      var u = el.getAttribute('data-full') || ((el.querySelector('img') || {}).currentSrc) || '';
      observe(el, {
        gcls: el.getAttribute('data-gcls-slide'),
        id: fileId(u) || ('slide-' + el.getAttribute('data-gcls-slide')),
        pos: (+el.getAttribute('data-gcls-slide') || 0) + 1, label: altLabel(el), pid: PID,
        bundle: true, spot: false
      });
    });
  }
  /* a frame whose picture changes in place (the Personalized iPhone Case stage): the component tells
     which picture it shows (gc:track media_show); the old one's time ends there */
  function mediaShow(d) {
    var el = d.el;
    if (!el || !el.nodeType) return;
    var t = el.__gcT || observe(el, { frame: true, pid: PID });
    var id = String(d.media_id || '');
    if (t.id === id) return;
    stop(t);
    t.id = id;
    t.pos = d.media_pos || 0;
    t.label = slug(d.media_label || '', 40);
    t.bundle = !!d.is_bundle_image;
    t.spot = !!d.has_initials_spot;
    update(t);
  }
  function stopAll() { var at = now(); targets.forEach(function (t) { stop(t, at); }); }
  D.addEventListener('visibilitychange', function () {
    if (visibleDoc()) targets.forEach(update); else stopAll();
  });
  W.addEventListener('pagehide', stopAll);

  function topOf(pidOnly) {
    var s = store(), best = null, n = 0, k, r;
    for (k in s.v) {
      r = s.v[k];
      if (pidOnly && r.pid !== pidOnly) continue;
      n++;
      if (!best || r.ms > best.ms) best = r;
    }
    var last = s.last && s.v[s.last];
    if (pidOnly && last && last.pid !== pidOnly) last = null;
    return { top: best, last: last, n: n };
  }
  function context(name, surface) {
    var at = now();
    targets.forEach(function (t) { if (t.since) { stop(t, at); update(t); } }); /* bank the current image's time */
    var c = PID ? topOf(PID) : { n: 0 };
    if (!c.n) c = topOf('');
    push(name, {
      surface: surface || PAGE,
      top_image: c.top ? c.top.id : undefined,
      top_image_pos: c.top ? c.top.pos : undefined,
      top_image_ms: c.top ? c.top.ms : undefined,
      last_image: c.last ? c.last.id : undefined,
      images_viewed: c.n
    }, { quiet: true });
    if (c.top && typeof W.clarity === 'function') {
      try { W.clarity('set', 'gc_top_image', String(c.top.id)); } catch (e) {}
    }
  }

  /* add to cart: Elevar's dl_add_to_cart, after the theme's add-on filter (an add of add-ons only is
     renamed dl_gc_addon_added and does not count). Read from the dataLayer, never changed. */
  var lastAdd = 0, lastCheckout = 0, upsell = null;
  function watchDataLayer() {
    var dl = W.dataLayer = W.dataLayer || [];
    if (W.__gcDL === dl) return; /* once per array: GTM keeps the push it finds and calls it */
    W.__gcDL = dl;
    var orig = dl.push;
    var wrapped = function () {
      var r = orig.apply(this, arguments);
      try {
        for (var i = 0; i < arguments.length; i++) {
          var e = arguments[i];
          if (e && e.event === 'dl_add_to_cart') setTimeout(added, 0);
        }
      } catch (x) {}
      return r;
    };
    dl.push = wrapped;
  }
  /* one context per add: Elevar's dl_add_to_cart, or a builder's own add event (the builders add through
     the kit's XMLHttpRequest, which Elevar may report late or not at all); whichever comes first */
  function added() {
    if (now() - lastAdd < 8000) return;
    lastAdd = now();
    context('add_to_cart_context');
    /* an add within 5 minutes of opening a "Complete the look" item came from it */
    if (upsell && now() - upsell.at < 300000) { push('cart_upsell_add', { mode: upsell.mode }); upsell = null; }
  }
  function checkout(surface) {
    if (now() - lastCheckout < 3000) return;
    lastCheckout = now();
    context('checkout_context', surface);
  }

  /* ---------------------------------------------------------------- initials typed on the builders */
  /* the builders report every change of the field (gc:track initials_input, length only); the event is
     sent once the shopper stops typing for 1.5 s, or leaves the page */
  var iniT = {}, iniSent = {};
  function iniInput(d) {
    var s = d.surface || PAGE, n = +d.initials_len || 0;
    clearTimeout(iniT[s]);
    iniT[s] = setTimeout(function () { iniFlush(s, n); }, 1500);
    iniT[s + '_n'] = n;
  }
  function iniFlush(s, n) {
    if (n === undefined) n = iniT[s + '_n'];
    if (n === undefined) return;
    var was = iniSent[s] || 0;
    if (n === was) return;
    iniSent[s] = n;
    if (n > 0) push('initials_set', { surface: s, initials_len: n, initials_fee: feeTier(n), action: was ? 'edit' : 'apply' });
    else push('initials_remove', { surface: s });
  }
  W.addEventListener('pagehide', function () { for (var s in iniT) if (!/_n$/.test(s)) { clearTimeout(iniT[s]); iniFlush(s); } });

  /* ---------------------------------------------------------------- clicks (one delegated listener) */
  var spotAt = 0;
  function addonName(el) {
    if (el.closest('[data-gc-gift-addon]')) return 'gift_packaging';
    if (el.closest('[data-gc-priority-addon]')) return 'priority_processing';
    if (el.closest('[data-gc-sp-addon]')) return 'screen_protector';
    if (el.closest('[data-gc-ms-addon]')) return 'magsafe_wallet';
    return 'other';
  }
  function mediaOf(el) {
    var m = el.closest('[data-media-id]');
    return m ? { id: m.getAttribute('data-media-id'), pos: +m.getAttribute('data-media-position') || undefined } : {};
  }
  function qParam(href, k) {
    try { return new URL(href, location.href).searchParams.get(k) || undefined; } catch (e) { return undefined; }
  }
  function onClick(e) {
    var t = e.target, el;
    if (!t || !t.closest) return;
    if ((el = t.closest('[data-gcpx-ps-spot]'))) { spotAt = now(); return; }
    if ((el = t.closest('[data-open-personalize-drawer], [data-open-personalize-floating]'))) {
      var fromSpot = now() - spotAt < 800;
      var had = !!(W.currentPersonalization && W.currentPersonalization.initials);
      if (fromSpot) push('initials_spot_open', { surface: 'pdp_photo', action: had ? 'edit' : 'add' });
      push('personalize_open', {
        source: fromSpot ? 'photo_spot' : el.hasAttribute('data-open-personalize-floating') ? 'floating' : 'button',
        action: had ? 'edit' : 'add'
      });
      return;
    }
    if ((el = t.closest('[data-gc-set-tag]'))) {
      var m = mediaOf(el);
      push('bundle_link_click', { media_id: m.id, media_pos: m.pos, value_key: qParam(el.getAttribute('href'), 'leather') });
      return;
    }
    if ((el = t.closest('[data-gc-support]'))) { push('support_click', { surface: PAGE }); return; }
    if ((el = t.closest('[data-gc-addon-check]'))) {
      if (el.getAttribute('aria-disabled') === 'true') return;
      push('addon_toggle', { addon: addonName(el), state: el.getAttribute('aria-checked') === 'true' ? 'off' : 'on', surface: PAGE });
      return;
    }
    if ((el = t.closest('[data-gc-sp-add]'))) {
      if (el.disabled) return;
      push('addon_toggle', { addon: addonName(el), state: 'on', surface: PAGE });
      return;
    }
    if ((el = t.closest('[data-gc-undo-go]'))) { push('cart_undo', { action: 'undo' }); return; }
    if ((el = t.closest('[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]'))) {
      checkout(el.closest('[data-gc-track-surface]') ? el.closest('[data-gc-track-surface]').getAttribute('data-gc-track-surface') : (PAGE === 'cart' ? 'cart_page' : 'cart_drawer'));
      return;
    }
    if ((el = t.closest('[data-gc-track]'))) {
      var name = el.getAttribute('data-gc-track');
      if (name === 'checkout') { checkout(el.getAttribute('data-gc-track-surface') || PAGE); return; }
      var p = {}, a = el.attributes;
      for (var i = 0; i < a.length; i++) if (a[i].name.indexOf('data-gc-p-') === 0) p[a[i].name.slice(10).replace(/-/g, '_')] = a[i].value;
      if (name === 'cart_upsell_open') upsell = { at: now(), mode: p.mode };
      push(name, p);
    }
  }
  /* on window in the capture phase, registered while the page is still parsing: it runs before every
     component's own listener, including ones that hold or stop a press (the Check your order guard) */
  W.addEventListener('click', onClick, { capture: true, passive: true });

  /* ---------------------------------------------------------------- component events */
  D.addEventListener('gc:track', function (e) {
    try {
      var d = (e && e.detail) || {};
      if (d.name === 'media_show') { mediaShow(d); return; }
      if (d.name === 'initials_input') { iniInput(d); return; }
      if (d.name === 'checkout') { checkout(d.surface); return; }
      var p = {};
      for (var k in d) if (k !== 'name' && k !== 'el') p[k] = d[k];
      push(d.name, p);
      if (d.name === 'builder_add') added();
    } catch (x) {}
  });
  /* the PDP personalization drawer (assets/personalize-drawer.js) */
  var pzdHad = false;
  D.addEventListener('personalization:added', function (e) {
    var d = (e && e.detail) || {}, n = iniLen(d.initials);
    if (!n) return;
    push('initials_set', { surface: 'pdp_drawer', initials_len: n, initials_fee: feeTier(n), action: pzdHad ? 'edit' : 'apply' });
    pzdHad = true;
  });
  /* the drawer also clears itself after the case and its fee went to the cart: that is not a removal,
     so a removal is only sent when no add happened within 10 s around it */
  var lastAdded = 0;
  D.addEventListener('product:added', function () { lastAdded = now(); });
  D.addEventListener('personalization:removed', function () {
    if (!pzdHad) return;
    pzdHad = false;
    var at = now();
    setTimeout(function () {
      if (Math.abs(lastAdded - at) < 10000 || Math.abs(lastAdd - at) < 10000) return;
      push('initials_remove', { surface: 'pdp_drawer' });
    }, 4000);
  });

  /* the Leather Set page's own add event (assets/gcls-set.js): one line per piece, tied by _set_id */
  D.addEventListener('gcls:added', function (e) {
    try {
      var d = (e && e.detail) || {}, items = d.items || [], pieces = 0, ini = 0;
      items.forEach(function (it) {
        var pr = (it && it.properties) || {};
        if (pr._set_id && !pr._personalization_link && !pr.For) pieces++;
        if (pr.Personalization) ini = Math.max(ini, iniLen(pr.Personalization));
      });
      push('builder_add', { builder: 'set', again: !!d.again, pieces: d.pieces || pieces || items.length, initials_len: ini || undefined });
      added();
    } catch (x) {}
  });

  /* the cart drawer (snippets/cart-drawer.liquid: data-section-type="cart" with aria-hidden) opening.
     Its lines are read a moment later, after the drawer has drawn the fresh cart. */
  function watchDrawer() {
    var dr = D.querySelector('[data-section-type="cart"][aria-hidden]');
    if (!dr || dr.__gcW || !W.MutationObserver) return;
    dr.__gcW = 1;
    var openAt = 0;
    new MutationObserver(function () {
      if (dr.getAttribute('aria-hidden') !== 'false') { openAt = 0; return; }
      if (openAt) return;
      var at = openAt = now();
      setTimeout(function () {
        if (openAt !== at) return;
        var keys = {};
        [].forEach.call(dr.querySelectorAll('[data-line-key]'), function (x) { keys[x.getAttribute('data-line-key')] = 1; });
        push('cart_drawer_open', { lines: Object.keys(keys).length, has_initials_thumb: !!dr.querySelector('[data-gc-ci-ini]') });
      }, 1200);
    }).observe(dr, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  /* the support tile's clip: first play in view, and the first full loop */
  function bindClips() {
    [].forEach.call(D.querySelectorAll('[data-gc-support-clip]'), function (v) {
      if (v.__gcV) return;
      v.__gcV = { played: false, done: false, t: 0 };
      v.addEventListener('playing', function () {
        if (v.__gcV.played) return;
        v.__gcV.played = true;
        push('support_video', { action: 'play', surface: PAGE });
      }, { passive: true });
      v.addEventListener('timeupdate', function () {
        var s = v.__gcV, c = v.currentTime || 0;
        if (!s.done && s.played && (c + 0.05 < s.t || v.ended)) {
          s.done = true;
          push('support_video', { action: 'complete', surface: PAGE });
        }
        s.t = c;
      }, { passive: true });
    });
  }

  function boot() {
    try { watchDataLayer(); } catch (e) {}
    try { scanImages(); } catch (e) {}
    try { bindClips(); } catch (e) {}
    try { watchDrawer(); } catch (e) {}
    try { targets.forEach(update); } catch (e) {}
  }
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', boot);
  else boot();
  /* Elevar loads GTM after this file on some pages; GTM keeps the push it finds, so the read above
     survives either order. Checked again once the page has loaded, in case the array was replaced. */
  W.addEventListener('load', function () {
    try { watchDataLayer(); scanImages(); bindClips(); watchDrawer(); targets.forEach(update); } catch (e) {}
    /* the gallery's carousel may start after load: look at the selected slide again once it has */
    setTimeout(function () { carWait = false; try { targets.forEach(update); } catch (e) {} }, 1500);
  });

  W.__gcTrack = { push: push, log: log, version: '1.0.0' };
})();
